import { selectPickerIndustries, type PickerIndustry } from "@nexus/contracts";

/**
 * Catalog row for the Identity Hub add list. Activity fields are optional so a
 * response from before those fields shipped can stay visible during rollout.
 */
export type AssignableIndustry = {
  index: string;
  title: string;
  assetCount90d?: number | null;
  dataUpdated?: string | null;
};

function isLegacyCatalogRow(row: AssignableIndustry): boolean {
  return row.assetCount90d === undefined && row.dataUpdated === undefined;
}

function toPositiveCountRow<T extends AssignableIndustry>(
  row: T,
): (T & PickerIndustry) | undefined {
  if (typeof row.assetCount90d !== "number" || row.assetCount90d <= 0) return undefined;
  return { ...row, assetCount90d: row.assetCount90d, dataUpdated: row.dataUpdated ?? null };
}

function sortByTitle<T extends { title: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * Display order for an org's assigned industries. Does not reorder the saved
 * selection: callers pass the result to render only, so opening the drawer
 * does not look like an unsaved change.
 */
export function sortIndicesByIndustryTitle(
  indices: string[],
  titleByIndex: ReadonlyMap<string, string>,
): string[] {
  const titleOf = (index: string) => titleByIndex.get(index) ?? index;
  return [...indices].sort((a, b) => titleOf(a).localeCompare(titleOf(b)));
}

/**
 * Identity Hub add-list only. Explicit null and zero counts are inactive and are
 * removed before duplicate collapse. Rows that omit both activity fields stay
 * unfiltered so an older catalog payload does not disappear. Assigned rows still
 * resolve from the full `/industries/all` list.
 */
export function selectAssignableIndustries<T extends AssignableIndustry>(industries: T[]): T[] {
  const legacy: T[] = [];
  const positiveCounts: Array<T & PickerIndustry> = [];
  for (const row of industries) {
    if (isLegacyCatalogRow(row)) {
      legacy.push(row);
      continue;
    }
    const positive = toPositiveCountRow(row);
    if (positive) positiveCounts.push(positive);
  }

  const collapsed = selectPickerIndustries(positiveCounts);
  if (legacy.length === 0) return collapsed;
  if (collapsed.length === 0) return sortByTitle(legacy);
  return sortByTitle([...collapsed, ...legacy]);
}
